import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CardGiftcard as PackageIcon,
  TrendingUp as SavingsIcon,
} from '@mui/icons-material';
import JuntoApiService, { CreditPackage, CreditPackageItem, Credit } from '../services/api';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const CreditPackagesPage: React.FC = () => {
  // State
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [credits, setCredits] = useState<Credit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<CreditPackage | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    items: [] as CreditPackageItem[],
    package_price: 0,
    max_purchases_per_user: null as number | null,
    display_order: 0,
    image_url: '',
    tags: [] as string[],
  });

  // Load data
  useEffect(() => {
    loadAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [packagesData, creditsData] = await Promise.all([
        JuntoApiService.getCreditPackages(includeInactive),
        JuntoApiService.getCredits(),
      ]);
      
      setPackages(packagesData);
      setCredits(creditsData);
      setError(null);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Filter packages based on search term
  const filteredPackages = packages.filter(pkg =>
    pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pkg.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pkg.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination
  const paginatedPackages = filteredPackages.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Helper functions
  const getCreditName = (id: string) => credits.find(c => c.id === id)?.name || 'Unknown';
  
  const calculateIndividualValue = (items: CreditPackageItem[]) => {
    return items.reduce((total, item) => {
      const credit = credits.find(c => c.id === item.credit_id);
      const creditCost = credit ? credit.cost : 0;
      return total + (creditCost * item.quantity);
    }, 0);
  };
  
  const calculateSavings = (packagePrice: number, items: CreditPackageItem[]) => {
    const individualValue = calculateIndividualValue(items);
    return Math.max(0, individualValue - packagePrice);
  };
  
  const calculateSavingsPercentage = (packagePrice: number, items: CreditPackageItem[]) => {
    const individualValue = calculateIndividualValue(items);
    if (individualValue <= 0) return 0;
    return Math.round(((individualValue - packagePrice) / individualValue) * 100);
  };

  // Dialog handlers
  const openCreateDialog = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      description: '',
      items: [],
      package_price: 0,
      max_purchases_per_user: null,
      display_order: 0,
      image_url: '',
      tags: [],
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (pkg: CreditPackage) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      items: pkg.items,
      package_price: pkg.package_price,
      max_purchases_per_user: pkg.max_purchases_per_user ?? null,
      display_order: pkg.display_order,
      image_url: pkg.image_url || '',
      tags: pkg.tags,
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingPackage(null);
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      if (formData.items.length === 0) {
        setDialogError('Package must contain at least one credit item');
        return;
      }

      if (editingPackage) {
        // Only send the fields that can be updated, never send metadata like created_at/updated_at
        const packageData = {
          id: editingPackage.id, // ID is needed for the API call
          ...formData,
          // Don't include created_at, updated_at - these are managed by the server
        };
        await JuntoApiService.updateCreditPackage(packageData as CreditPackage);
      } else {
        await JuntoApiService.createCreditPackage(formData);
      }
      
      await loadAllData();
      closeDialog();
    } catch (err) {
      console.error('Failed to save package:', err);
      setDialogError('Failed to save package. Please check your input.');
    }
  };


  const addCreditItem = () => {
    if (credits.length === 0) return;
    
    const firstCredit = credits[0];
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          credit_id: firstCredit.id,
          quantity: 1
        }
      ]
    });
  };

  const updateCreditItem = (index: number, updates: Partial<CreditPackageItem>) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], ...updates };
    setFormData({ ...formData, items: newItems });
  };

  const removeCreditItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading credit packages...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Credit Packages</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Create Package
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={loadAllData} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PackageIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{packages.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Packages
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SavingsIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    ${Math.round(packages.reduce((sum, p) => sum + p.package_price, 0) / packages.length || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Price
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SavingsIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {packages.filter(p => p.is_active).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Search packages, descriptions, tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ flexGrow: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label="Include Inactive"
          />
        </Box>
      </Paper>

      {/* Packages Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Package Name</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Savings</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPackages.map((pkg) => (
                <TableRow key={pkg.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {pkg.name}
                      </Typography>
                      {pkg.description && (
                        <Typography variant="body2" color="text.secondary">
                          {pkg.description}
                        </Typography>
                      )}
                      {pkg.tags.length > 0 && (
                        <Box mt={1}>
                          {pkg.tags.map((tag: string) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      {pkg.items.map((item: CreditPackageItem, index: number) => (
                        <Typography key={index} variant="body2">
                          {item.quantity}x {getCreditName(item.credit_id)}
                        </Typography>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        ${pkg.package_price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Value: ${calculateIndividualValue(pkg.items)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" color="success.main" fontWeight="medium">
                        ${calculateSavings(pkg.package_price, pkg.items)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {calculateSavingsPercentage(pkg.package_price, pkg.items)}% off
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pkg.is_active ? 'Active' : 'Inactive'}
                      color={pkg.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={pkg}
                      entityType="credit-packages"
                      entityDisplayName="Credit Package"
                      onEdit={openEditDialog}
                      onRefresh={loadAllData}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          {entity.description && (
                            <Typography variant="body1">
                              <strong>Description:</strong> {entity.description}
                            </Typography>
                          )}
                          <Typography variant="body1">
                            <strong>Price:</strong> ${entity.package_price}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Items:</strong> {entity.items.length} credit types
                          </Typography>
                          <Typography variant="body1">
                            <strong>Active:</strong> {entity.is_active ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPackages.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="lg" fullWidth>
          <DialogTitle>
            {editingPackage ? 'Edit Credit Package' : 'Create New Credit Package'}
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Package Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Display Order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                />
              </Grid>

              {/* Credit Items */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
                  <Typography variant="h6">Credit Items</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addCreditItem}
                    disabled={credits.length === 0}
                  >
                    Add Item
                  </Button>
                </Box>
                
                {formData.items.length === 0 ? (
                  <Alert severity="info">
                    Add credit items to create a package. Each item specifies a credit type and quantity.
                  </Alert>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <List>
                      {formData.items.map((item, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <Box display="flex" gap={2} alignItems="center" width="100%">
                              <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Credit Type</InputLabel>
                                <Select
                                  value={item.credit_id}
                                  onChange={(e) => {
                                    updateCreditItem(index, {
                                      credit_id: e.target.value
                                    });
                                  }}
                                >
                                  {credits.map((credit) => (
                                    <MenuItem key={credit.id} value={credit.id}>
                                      {credit.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              
                              <TextField
                                label="Quantity"
                                type="number"
                                value={item.quantity || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const quantity = value === '' ? 0 : parseInt(value);
                                  updateCreditItem(index, {
                                    quantity: isNaN(quantity) ? 1 : quantity
                                  });
                                }}
                                onBlur={(_e) => {
                                  // Ensure minimum of 1 on blur if empty or 0
                                  if (!item.quantity || item.quantity < 1) {
                                    updateCreditItem(index, { quantity: 1 });
                                  }
                                }}
                                inputProps={{ min: 1 }}
                                sx={{ width: 100 }}
                              />
                              
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                                {(() => {
                                  const credit = credits.find(c => c.id === item.credit_id);
                                  const unitCost = credit ? credit.cost : 0;
                                  return `$${unitCost.toFixed(2)} × ${item.quantity} = $${(item.quantity * unitCost).toFixed(2)}`;
                                })()}
                              </Typography>
                            </Box>
                            
                            <ListItemSecondaryAction>
                              <IconButton onClick={() => removeCreditItem(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < formData.items.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                    
                    <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="body1">
                        <strong>Total Individual Value: ${calculateIndividualValue(formData.items).toFixed(2)}</strong>
                      </Typography>
                      {formData.package_price > 0 && (
                        <Typography variant="body2" color="success.main">
                          Savings: ${calculateSavings(formData.package_price, formData.items).toFixed(2)} 
                          ({calculateSavingsPercentage(formData.package_price, formData.items)}% off)
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                )}
              </Grid>

              {/* Pricing */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Pricing</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Package Price ($)"
                  type="number"
                  value={formData.package_price}
                  onChange={(e) => setFormData({ ...formData, package_price: parseFloat(e.target.value) || 0 })}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Purchases Per User"
                  type="number"
                  value={formData.max_purchases_per_user || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    max_purchases_per_user: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  inputProps={{ min: 1 }}
                  helperText="Leave empty for unlimited"
                />
              </Grid>

              {/* Tags */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tags (comma-separated)"
                  defaultValue={formData.tags.join(', ')}
                  onBlur={(e) => {
                    const tags = e.target.value
                      .split(',')
                      .map(tag => tag.trim())
                      .filter(tag => tag.length > 0);
                    setFormData({ ...formData, tags });
                  }}
                  placeholder="e.g. bundle, popular, savings"
                  helperText="Enter tags separated by commas for categorization and filtering"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {editingPackage ? 'Update Package' : 'Create Package'}
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default CreditPackagesPage;